import "./Link.scss";
import cn from "classnames";
import React, { JSX } from "react";
import { LinkProps } from "/Models/Generated/LinkProps";
import { IconProps } from "/Components/Icons/IconProps";
import { ColorTheme } from "/Models/Models/ColorTheme";
import { createSpreadableBem } from "/Helpers/createBem";

export interface LinkWithIconProps {
  icon?: React.FC<IconProps>;
  iconThickness?: "Reg" | "Light" | "Thin";
  iconPlacement?: "before" | "after";
  hoverIcon?: React.FC<IconProps>;
  hoverIconColor?: ColorTheme;
  colorTheme?: ColorTheme;
  size?: "x-small" | "small" | "medium";
  fixedIcon?: boolean;
  outlineColor?: "white" | "default";
  hasUnderline?: boolean;
  tabIndex?: number;
  iconSize?: IconProps["size"];
  contained?: boolean;
  breakWord?: boolean;
  download?: boolean;
  as?: keyof JSX.IntrinsicElements;
}

export interface ExtendedLinkProps extends LinkWithIconProps, LinkProps {}

const Link = ({
  href,
  target,
  text,
  colorTheme = ColorTheme.default,
  size = "medium",
  fixedIcon = false,
  outlineColor = "default",
  icon,
  iconThickness,
  iconPlacement,
  hoverIcon,
  hoverIconColor,
  hasUnderline = false,
  tabIndex,
  iconSize = "20",
  contained,
  breakWord,
  download,
  as: WrapperEl = "div",
}: ExtendedLinkProps) => {
  if ((icon || hoverIcon) && !iconPlacement) {
    console.error("iconPlacement is required when using icon or hoverIcon");
    return null;
  }

  if (!href) {
    return null;
  }

  const bem = createSpreadableBem("link-norad");

  const IconComp = icon;
  const IconWhenHover = hoverIcon;

  return (
    <WrapperEl
      {...bem("", {
        "--theme-default": colorTheme === ColorTheme.default,
        "--theme-green": colorTheme === ColorTheme.green,
        "--theme-dark-green": colorTheme === ColorTheme.darkGreen,
        "--theme-black": colorTheme === ColorTheme.black,
        "--x-small": size === "x-small",
        "--small": size === "small",
        "--medium": size === "medium",
        "--fixed-icon": fixedIcon,
        "--white-outline": outlineColor === "white",
        "--has-underline": hasUnderline,
        "--break-word": breakWord,
      })}
    >
      {IconComp && iconPlacement === "before" && (
        <>
          <IconComp
            thickness={iconThickness ?? "Reg"}
            size={iconSize}
            color={colorTheme}
          />
          {IconWhenHover && (
            <IconWhenHover
              thickness={iconThickness ?? "Reg"}
              size={iconSize}
              color={hoverIconColor ?? ColorTheme.default}
            />
          )}
        </>
      )}
      <a
        className={cn(
          "link-norad__container",
          !!contained && "link-norad__container--contained",
          !!hoverIcon && "link-norad__container--hoverable",
        )}
        data-testid="link-as-link"
        {...{ target, href }}
        tabIndex={tabIndex}
        download={download}
      >
        <span className="link-norad__container__text">{text}</span>
      </a>
      {IconComp && iconPlacement === "after" && (
        <>
          <IconComp
            thickness={iconThickness ?? "Reg"}
            size={iconSize}
            color={colorTheme}
          />
          {IconWhenHover && (
            <IconWhenHover
              thickness={iconThickness ?? "Reg"}
              size={iconSize}
              color={hoverIconColor ?? ColorTheme.default}
            />
          )}
        </>
      )}
    </WrapperEl>
  );
};

export default Link;
